// taken from https://medium.com/@wikycheng/handle-duplicate-ajax-request-and-abort-the-pending-previous-http-call-when-route-changing-using-3706d769f9e6
import axios from 'axios'

import config from '@/utils/config.js'
import {
  CSRF_TOKEN_HEADER,
  captureCsrfToken,
  getCsrfToken,
  isInternalRequest,
  isStateChangingMethod,
} from '@/utils/csrfToken.js'
import { attachFingerprint } from '@/utils/fingerprintInterceptor.js'

const RAILS_DOMAIN = config('RAILS_DOMAIN')

const service = axios.create({
  baseURL: `https://${RAILS_DOMAIN}/`, // API url
  withCredentials: true,
  timeout: 30 * 1000, // API request timeout set to 30s
})

// abort duplicate request
const pending = {}
const CancelToken = axios.CancelToken
const removePending = (config, f) => {
  if (config) {
    // make sure the url is same for both request and response
    const url = config.url.replace(config.baseURL, '/')
    // stringify whole RESTful request with URL params
    const flagUrl = `${url}&${config.method}&${JSON.stringify(config.params)}`
    if (flagUrl in pending) {
      if (f) {
        f() // abort the request
      } else {
        delete pending[flagUrl]
      }
    } else {
      if (f) {
        pending[flagUrl] = f // store the cancel function
      }
    }
  }
}

/**
 * Attaches the session's CSRF token to a request that needs one (PLT-2103).
 *
 * Three conditions, all required, and none of them ever blocks: a request with
 * no token stored goes out exactly as it does today. Nothing here waits for,
 * fetches, or retries on a token — Rails supplies it on the next response and
 * the write after that carries it.
 *
 *  - **internal** — the token must never reach a third-party host. See
 *    `isInternalRequest`; this is the guard that survives someone routing an
 *    external call through this instance.
 *  - **state-changing** — `GET`/`HEAD` carry no token.
 *  - **a token is stored** — captured off an earlier response.
 *
 * Mutates and returns `config`, which is what an axios request interceptor is.
 */
const attachCsrfToken = (config) => {
  const token = getCsrfToken()

  if (
    token &&
    isStateChangingMethod(config.method) &&
    isInternalRequest(config.url, config.baseURL)
  ) {
    // axios 0.24 models `config.headers` as a plain object — there is no
    // `AxiosHeaders#set` to go through.
    config.headers[CSRF_TOKEN_HEADER] = token
  }

  return config
}

/**
 * Registers the CSRF attach + capture interceptors on an axios instance.
 *
 * This exists because of an axios 0.24 detail: `instance.create()` calls
 * `createInstance(mergeConfig(defaultConfig, instanceConfig))`, which builds a
 * fresh `new Axios(...)`. The **config** is inherited (`baseURL`,
 * `withCredentials`); the **interceptors** are not. `src/api/index.js` forks the
 * shared instance that way, so without this helper that fork would be a silent
 * capture blind spot and a trap for the next write added there.
 *
 * `service` below does not go through this helper — its own interceptor pair
 * already owns the duplicate-request bookkeeping, and folding the same two
 * operations into that pair is deliberate: a second `.use()` on the same
 * instance could run after the cancel branch has already resolved `{}`. Both
 * paths call the same `attachCsrfToken` / `captureCsrfToken`, so there is one
 * source of truth for the behavior either way.
 *
 * Nothing is ever registered on the bare `axios` import, `axios.interceptors`,
 * or `axios.defaults`: the cloud-function POST
 * (`src/store/modules/reports/actions.js`) and the Hermes compile POST
 * (`src/services/hermesService.js`) use bare `axios`, and they must stay
 * untouched.
 */
export const applyCsrfInterceptors = (instance) => {
  instance.interceptors.request.use(attachCsrfToken)

  instance.interceptors.response.use(
    (response) => {
      captureCsrfToken(response.headers)
      return response
    },
    (error) => {
      captureCsrfToken(error.response?.headers)
      return Promise.reject(error)
    }
  )

  return instance
}

// axios interceptors
service.interceptors.request.use(
  async (config) => {
    // you can apply cancel token to all or specific requests
    // e.g. except config.method == 'options'
    config.cancelToken = new CancelToken((c) => {
      if (!config.dupe) {
        removePending(config, c)
      }
    })

    // The `X-Fingerprint` attach is folded into *this* handler rather than
    // registered as a second request interceptor (PLT-2288, Key Decision 6).
    // axios 0.x runs request interceptors in reverse registration order, so a
    // separately registered async interceptor would run ahead of this one and
    // delay the `cancelToken` assignment above by the fingerprint compute
    // (~100-500 ms on a cold browser), which would change the duplicate-request
    // bookkeeping's timing. Assigning the token first and awaiting after keeps
    // that bookkeeping exactly as it is today.
    //
    // `attachFingerprint` never rejects: a build with the flag off, a request to
    // a third-party host, or a browser that cannot produce a visitor ID all
    // resolve to this same config, untouched.
    return attachFingerprint(attachCsrfToken(config), service.defaults.baseURL)
  },
  (error) => {
    Promise.reject(error)
  }
)

service.interceptors.response.use(
  (response) => {
    // Capture before the bookkeeping below, so the token is stored no matter
    // what the rest of the arm decides to do with the response.
    captureCsrfToken(response.headers)

    removePending(response.config)
    return response
  },
  (error) => {
    // Both arms capture. A session whose recent traffic is all 4xx must not go
    // tokenless — the error arm is exactly the case PLT-2102 flagged. This runs
    // ahead of the `isCancel` branch below because that branch resolves `{}`
    // and ends the chain; a cancelled request has no response to read anyway,
    // and `captureCsrfToken` no-ops on nullish headers.
    captureCsrfToken(error.response?.headers)

    removePending(error.config)

    if (!axios.isCancel(error)) {
      return Promise.reject(error)
    } else {
      // return empty object for aborted request
      return Promise.resolve({})
    }
  }
)

service.objectToQuery = (obj) => {
  return Object.keys(obj)
    .reduce((a, k) => {
      a.push(k + '=' + encodeURIComponent(obj[k]))
      return a
    }, [])
    .join('&')
}

export default service
